import {
  createBrowserRouter,
  Navigate,
  Outlet,
  type RouteObject,
  RouterProvider,
  RoutesProvider,
  ScrollRestoration,
} from '@faceup/router'
import { useContext, useEffect } from 'react'
import EmailVerification from '../Components/EmailVerification'
import { InstitutionLayout } from '../Components/InstitutionLayout'
import NavigationChange from '../Components/NavigationChange'
import PointerEventsMember from '../Components/PointerEventsMember'
import { RouterError } from '../Components/RouterError'
import { RouterFullScreenLoader } from '../Components/RouterFullScreenLoader'
import { SurveyLayout } from '../Components/SurveyLayout/SurveyLayout'
import { LanguageContext } from '../Contexts/LanguageContext'
import { LayoutContext } from '../Contexts/LayoutContext'
import SurveySourceProvider from '../Contexts/SurveySourceProvider'
import { UserContext } from '../Contexts/UserContext'
import { useRoutes } from '../hooks/useRoutes'
import { useSavedReportsContext } from '../hooks/useSavedReportsContext'
import useCustomizationByURLParams from '../utils/useCustomizationByURLParams'
import Biometrics from './Authentication/BiometricsPage'
import LandingPage from './Authentication/LandingPage'
import { AwaitReportSource } from './AwaitReportSource'
import { AwaitSurvey } from './AwaitSurvey'
import CheckReport from './CheckReport'
import { CheckReportList } from './CheckReportList'
import CreateReport from './CreateReport/CreateReport'
import Offline from './Offline/Offline'
import DefaultPageRedirect from './Page/DefaultPageRedirect'
import PageView from './Page/PageView'
import Report from './Report/Report'
import ReportSent from './ReportSent'
import SearchInstitution from './SearchInstitution/SearchInstitution'
import SurveyView from './Survey/SurveyView'
import SurveySent from './SurveySent/SurveySent'

const AppRouter = () => {
  const { basename } = useContext(LanguageContext)
  const { reportSource, isAuthenticated, isSetUp } = useContext(UserContext)
  const { setClient, client } = useContext(LayoutContext)
  const isApp = client === 'app'
  const { hasSomeSavedReports } = useSavedReportsContext()
  const { customizeClient } = useCustomizationByURLParams(window.location.search)
  const routeLinks = useRoutes()

  // biome-ignore lint/correctness/useExhaustiveDependencies: Only run on mount to customize client once
  useEffect(() => {
    const client = customizeClient()
    if (client) {
      setClient(client)
    }
  }, [])

  const surveyRoutes: RouteObject[] = [
    {
      path: 'q/:shortId',
      element: (
        <SurveySourceProvider>
          <Outlet />
        </SurveySourceProvider>
      ),
      children: [
        {
          index: true,
          element: (
            <SurveyLayout headerVariant='simple-no-back-button'>
              <SurveyView />
            </SurveyLayout>
          ),
        },
        {
          path: 'sent',
          element: (
            <SurveyLayout headerVariant='simple-no-back-button'>
              <SurveySent />
            </SurveyLayout>
          ),
        },
      ],
    },
  ]

  const reportSourceRoutes: RouteObject[] = [
    {
      path: 's/:shortId',
      element: <Navigate to={routes => routes.page()} />,
    },
    {
      path: 'c/:shortId',
      element: <Navigate to={routes => routes.page()} />,
    },
    {
      path: 'c/:shortId/create',
      element: (
        <InstitutionLayout variant='simple' topAligned>
          <CreateReport />
        </InstitutionLayout>
      ),
    },
    {
      path: 'c/:shortId/sent',
      element: (
        <InstitutionLayout variant='simple'>
          <ReportSent />
        </InstitutionLayout>
      ),
    },
    {
      path: 'c/:shortId/check',
      element: (
        <InstitutionLayout variant='simple'>
          {isApp && hasSomeSavedReports ? <CheckReportList /> : <CheckReport />}
        </InstitutionLayout>
      ),
    },
    ...(isApp
      ? [
          {
            path: 'c/:shortId/check/form',
            element: (
              <InstitutionLayout variant='simple'>
                <CheckReport />
              </InstitutionLayout>
            ),
          },
        ]
      : []),
    {
      path: 'c/:shortId/report',
      element: (
        <InstitutionLayout variant='simple'>
          <Report />
        </InstitutionLayout>
      ),
    },
    {
      path: 'c/:shortId/page/:id',
      element: (
        <InstitutionLayout>
          <PageView />
        </InstitutionLayout>
      ),
    },
    { path: 'c/:shortId/page', Component: DefaultPageRedirect },
  ]

  const getShortLinkRoutes: () => RouteObject[] = () => {
    return [
      {
        path: '*',
        element: (
          <AwaitSurvey>
            <Outlet />
          </AwaitSurvey>
        ),
        children: [...surveyRoutes],
      },
      {
        path: '*',
        element: (
          <AwaitReportSource>
            <Outlet />
          </AwaitReportSource>
        ),
        children: [...reportSourceRoutes],
      },
    ]
  }

  const routes: RouteObject[] = [
    {
      path: '',
      element: !isSetUp() ? (
        <Navigate to={routes => routes.settingsBiometrics()} />
      ) : reportSource?.institutionId ? (
        <Navigate to={routes => routes.page({ shortId: reportSource.shortLink })} />
      ) : (
        <SearchInstitution />
      ),
    },
    { path: 'token/:id', Component: EmailVerification },
    ...(isApp ? [{ path: 'settings/biometrics', Component: Biometrics }] : []),
    { path: 'offline', Component: Offline },
    {
      path: '*',
      element: (
        <Navigate
          to={routes => routes.home()} // replace
        />
      ),
    },
  ]

  const router = createBrowserRouter([
    {
      path: `${basename}`,
      element: (
        <RoutesProvider routes={routeLinks}>
          <NavigationChange />
          <PointerEventsMember>
            <ScrollRestoration />
            <Outlet />
          </PointerEventsMember>
        </RoutesProvider>
      ),
      children: [...routes, ...getShortLinkRoutes()],
      errorElement: <RouterError />,
    },
    { path: '/*', Component: RouterFullScreenLoader },
  ])

  return (
    <>
      {/* We want to keep Router rendered, therefore we only hide it with CSS
          When app enters backround and user is logged out, we want to keep
          the state of app including input states, thats why it is only hidden
       */}
      {!isAuthenticated && <LandingPage />}
      <div className={isAuthenticated ? '' : 'hidden absolute top-0 max-h-screen overflow-hidden'}>
        <RouterProvider router={router} />
      </div>
    </>
  )
}

export default AppRouter
